import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { store } from '@store';
import { ehrQueryOptions, useCreateEhr } from '@patient/queries/ehrQueries';
import { DateHelper, DateFormat } from '@utils/DateHelper';

import { profileForm } from '../profileForm';

interface Params {
    redirectOnCreate?: boolean;
    onCreate?(id?: string): void;
    onCancelCreate(): void;
}

interface Data {
    isEhrExist: boolean;
    onSexChange(value: string): void;
    onBirthDateChange(date: Date | string): void;
    onSaveButtonClick(): void;
    onCancelButtonClick(): void;
    onUseExistingEhrClick(): void;
    onExistingEhrCloseButtonClick(): void;
}

export function useCreateEhrForm({ redirectOnCreate, onCreate, onCancelCreate }: Params): Data {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const createEhrMutation = useCreateEhr();

    const [existingEhrRecordId, setExistingEhrRecordId] = useState<number | undefined>(undefined);
    const onSaveModeRef = useRef(false);

    const resetForm = useCallback(() => {
        profileForm.reset();
        store.app.isOnValidation = false;
    }, []);

    const clearChanges = () => {
        resetForm();
        setExistingEhrRecordId(undefined);
        onSaveModeRef.current = false;
    };

    const isCreateEhrExist = async (): Promise<boolean> => {
        const createParams = profileForm.getCreateParams();

        const existingEhrs = await queryClient.fetchQuery(
            ehrQueryOptions.duplicates({
                firstName: createParams.firstName,
                lastName: createParams.lastName,
                patronymic: createParams.patronymic,
                birthDate: DateHelper.format(createParams.birthDate, DateFormat.DateWithDashSeparator),
            }),
        );

        const isExist = existingEhrs.length > 0;
        setExistingEhrRecordId(isExist ? existingEhrs[0].recordId : undefined);

        return isExist;
    };

    const createEhr = async (): Promise<void> => {
        const createParams = profileForm.getCreateParams();

        await createEhrMutation.mutateAsync(createParams);
        await onCreate?.(createParams.id);

        if (redirectOnCreate) {
            const newEhr = await queryClient.fetchQuery(ehrQueryOptions.detail(createParams.id));
            navigate(`/patient/${newEhr.recordId}`);
        }

        clearChanges();
    };

    const processCreateEhr = async (): Promise<void> => {
        if (onSaveModeRef.current) {
            return;
        }

        onSaveModeRef.current = true;
        store.app.isOnUpdate = true;

        const isExist = await isCreateEhrExist();
        if (!isExist) {
            await createEhr();
        }

        onSaveModeRef.current = false;
        store.app.isOnUpdate = false;
    };

    const onSexChange = (value: string): void => {
        profileForm.setFieldValue('sex', value);
    };

    const onBirthDateChange = (newDate: Date | string): void => {
        profileForm.setFieldValue('birthDate', newDate as string);
    };

    const onSaveButtonClick = async (): Promise<void> => {
        if (profileForm.isFormValid) {
            await processCreateEhr();
        }

        store.app.isOnValidation = true;
    };

    const onCancelButtonClick = (): void => {
        clearChanges();
        onCancelCreate();
    };

    const onUseExistingEhrClick = async (): Promise<void> => {
        if (existingEhrRecordId) {
            if (onCreate) {
                const { id } = await queryClient.fetchQuery(ehrQueryOptions.detail(String(existingEhrRecordId)));
                onCreate(id);
            } else {
                navigate(`/patient/${existingEhrRecordId}`);
            }
        }

        clearChanges();
    };

    const onExistingEhrCloseButtonClick = (): void => {
        setExistingEhrRecordId(undefined);
    };

    useEffect(() => {
        store.app.isOnValidation = false;
    }, []);

    useEffect(() => () => resetForm(), [resetForm]);

    return {
        isEhrExist: !!existingEhrRecordId,
        onSexChange,
        onBirthDateChange,
        onSaveButtonClick,
        onCancelButtonClick,
        onUseExistingEhrClick,
        onExistingEhrCloseButtonClick,
    };
}
